import { useContext } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import { Body, Cell, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconSparePart from '@/icons/SparePart.svg';
import { AgreementFromGetById } from '@/models/agreements';
import { useSparePartTypesOrganization } from '@/models/sparePartType';
import { useDatabaseTranslation } from '@/services/i18n';

import { AgreementEditContext } from '../AgreementEditContext';

import {
  getPriceAgreementColumnWidths,
  PriceAgreementTableHeader,
  PriceAgreementTableRow,
  PriceAgreementTableRowEdit,
  PriceAgreementTableRowNew,
} from './PriceAgreementTableRow';

export const SparePartsTable = ({
  spareParts,
  agreement,
}: {
  spareParts: AgreementFromGetById['prices'];
  agreement: AgreementFromGetById;
}) => {
  const { _compare } = useDatabaseTranslation();
  const { draft } = useContext(AgreementEditContext);

  const withMargin = agreement.clientB;

  const sorted = spareParts.sort((a, b) =>
    _compare(a.sparePartTypeOrganization!.name!, b.sparePartTypeOrganization!.name!)
  );

  if (draft) {
    return <SparePartsTableEdit spareParts={spareParts} withMargin={withMargin} />;
  }

  return (
    <Table columnWidths={getPriceAgreementColumnWidths({ withMargin })} bordered>
      <PriceAgreementTableHeader withMargin={withMargin} />
      <Body>
        {sorted.map((price) => (
          <PriceAgreementTableRow key={price.id} price={price} withMargin={withMargin}>
            <SparePartNameCell price={price} />
          </PriceAgreementTableRow>
        ))}
        {sorted.length === 0 && (
          <Row>
            <Cell isWholeRow>
              <Paragraph color="secondary">
                <Trans id="settings.organization.entities.agreements.detail.empty.spare-parts">
                  No spare part prices on this agreement.
                </Trans>
              </Paragraph>
            </Cell>
          </Row>
        )}
      </Body>
    </Table>
  );
};

const SparePartsTableEdit = ({
  spareParts,
  withMargin,
}: {
  spareParts: AgreementFromGetById['prices'];
  withMargin: boolean;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { draft } = useContext(AgreementEditContext);

  const { data: sparePartTypes = [] } = useSparePartTypesOrganization();

  const missingSparePartTypes = sparePartTypes.filter(
    (sparePartType) =>
      !draft.prices.some((price) => price.sparePartTypeOrganizationId === sparePartType.id)
  );

  return (
    <Table columnWidths={getPriceAgreementColumnWidths({ withMargin, withActions: true })} bordered>
      <PriceAgreementTableHeader withActions withMargin={withMargin} />
      <Body>
        {spareParts.map((price) => (
          <PriceAgreementTableRowEdit key={price.id} price={price} withMargin={withMargin}>
            <SparePartNameCell price={price} />
          </PriceAgreementTableRowEdit>
        ))}
        <PriceAgreementTableRowNew
          placeholder={t({
            id: 'settings.organization.entities.agreements.detail.table.spare-parts.add',
            message: 'Add spare part',
          })}
          options={missingSparePartTypes.map((sparePartType) => ({
            label: _db(sparePartType.name),
            ...sparePartType,
          }))}
          onSelect={(value) => ({
            sparePartTypeOrganization: value,
            sparePartTypeOrganizationId: value.id,
          })}
          withMargin={withMargin}
        />
      </Body>
    </Table>
  );
};

const SparePartNameCell = ({ price }: { price: AgreementFromGetById['prices'][number] }) => {
  const { _db } = useDatabaseTranslation();

  return (
    <Stack row gap="0.35rem" alignItems="center" flexWrap="nowrap">
      <IconSparePart style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
      <Paragraph>{_db(price.sparePartTypeOrganization!.name!)}</Paragraph>
    </Stack>
  );
};
